import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Button, DatePicker, Form, Statistic, Card, Row, Col } from 'antd';
import { FilterOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import api from '../../services/api';
import { SL_Grid } from '../../components/grid/SL_Grid';

const { RangePicker } = DatePicker;

// Janela padrão do ranking: últimos 7 dias (incluindo hoje).
const DEFAULT_FROM = () => dayjs().subtract(6, 'day');
const DEFAULT_TO = () => dayjs();

// Colunas do ranking (por usuário no período).
const generalColumns = [
    { field: 'position', headerName: '#', width: 70, type: 'number' },
    {
        field: 'user_name',
        headerName: 'Usuário',
        flex: 1.4,
        minWidth: 180,
        valueGetter: ({ row }) => {
            const name = `${row?.first_name ?? ''} ${row?.last_name ?? ''}`.trim();
            return name || row?.email || `#${row?.user_id}`;
        },
    },
    {
        field: 'email',
        headerName: 'E-mail',
        flex: 1.5,
        minWidth: 200,
        valueGetter: ({ row }) => row?.email || '-',
    },
    { field: 'points', headerName: 'Pontos', width: 120, type: 'number' },
    { field: 'events', headerName: 'Eventos', width: 120, type: 'number' },
];

export default function ListPointsGeneral() {
    const { mobile_mode } = useContext(GeneralContext);
    const [form] = Form.useForm();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFiltersVisible, setIsFiltersVisible] = useState(false);
    const [totals, setTotals] = useState({ points: 0, events: 0, scoring: 0, total: 0 });

    // Mantém o range atual (default últimos 7 dias). Usado para re-aplicar
    // ao limpar filtros e como valor inicial do RangePicker.
    const currentRange = useCallback((values) => {
        if (values?.date_range && values.date_range[0] && values.date_range[1]) {
            return [dayjs(values.date_range[0]), dayjs(values.date_range[1])];
        }
        return [DEFAULT_FROM(), DEFAULT_TO()];
    }, []);

    const loadGeneral = useCallback(async (values) => {
        const [from, to] = currentRange(values);
        const params = {
            date_from: from.format('YYYY-MM-DD'),
            date_to: to.format('YYYY-MM-DD'),
        };
        setLoading(true);
        try {
            const { data } = await api.points.general(params);
            const list = data.rows || [];
            setRows(list);
            const points = list.reduce((acc, r) => acc + Number(r.points || 0), 0);
            const events = list.reduce((acc, r) => acc + Number(r.events || 0), 0);
            const scoring = list.filter((r) => Number(r.events || 0) > 0).length;
            setTotals({ points, events, scoring, total: list.length });
        } catch (e) {
            // erro tratado pelo interceptor
        } finally {
            setLoading(false);
        }
    }, [currentRange]);

    useEffect(() => {
        // Default: últimos 7 dias.
        const initial = [DEFAULT_FROM(), DEFAULT_TO()];
        form.setFieldsValue({ date_range: initial });
        loadGeneral({ date_range: initial });
    }, [loadGeneral, form]);

    const onSubmitFilters = (values) => loadGeneral(values);
    const onClearFilters = () => {
        const initial = [DEFAULT_FROM(), DEFAULT_TO()];
        form.setFieldsValue({ date_range: initial });
        loadGeneral({ date_range: initial });
    };

    return (
        <div className="flex flex-col flex-1 h-full">
            <div className={`flex w-full bg-fit-red-700 p-2 rounded-tl-[4px] rounded-tr-[4px] flex-wrap ${mobile_mode ? 'flex-col items-center gap-4' : 'gap-2'}`}>
                <div className="self-center text-white font-semibold text-base">
                    Ranking de Pontos
                    <span className="text-white/80 font-normal text-sm ml-2">(últimos 7 dias)</span>
                </div>
                <div className="flex-1 flex flex-wrap gap-2 justify-end">
                    <Button
                        type="default"
                        shape="circle"
                        onClick={() => setIsFiltersVisible((v) => !v)}
                        icon={!isFiltersVisible ? <FilterOutlined className="text-fit-red-800 flex items-center justify-center" /> : <CloseOutlined className="text-fit-red-800 flex items-center justify-center" />}
                    />
                </div>
            </div>

            {isFiltersVisible && (
                <div className="w-full border border-gray-300 bg-fit-gray-100 p-3">
                    <Form
                        form={form}
                        layout="inline"
                        onFinish={onSubmitFilters}
                        className="flex flex-wrap gap-4"
                    >
                        <Form.Item name="date_range" label="Período" className="m-0">
                            <RangePicker format="DD/MM/YYYY" />
                        </Form.Item>
                        <Form.Item className="m-0">
                            <Button type="primary" htmlType="submit" style={{ fontWeight: 'bold', borderRadius: 7 }}>
                                Buscar
                            </Button>
                        </Form.Item>
                        <Form.Item className="m-0">
                            <Button danger style={{ fontWeight: 'bold', borderRadius: 7 }} onClick={onClearFilters}>
                                Limpar
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            )}

            <div className="p-3">
                <Row gutter={[16, 16]}>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="Total de pontos" value={totals.points} />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="Total de eventos" value={totals.events} />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="Usuários pontuando" value={totals.scoring} />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="Total de usuários" value={totals.total} />
                        </Card>
                    </Col>
                </Row>
            </div>

            <div className="w-full flex-1 min-h-[400px] smb:min-h-0">
                <SL_Grid
                    rows={rows}
                    columns={generalColumns}
                    loading={loading}
                    pagination={false}
                    getRowId={(row) => row.user_id}
                />
            </div>
        </div>
    );
}
